import { useMemo, useState, useEffect } from "react";
import type { DaylightProfileValues } from "../../components/types";

export type DaylightProfileValidationErrors = Record<string, string>;

const DAYLIGHT_PROFILE_FIELD_CONFIG: Record<string, { min: number; max: number }> = {
  baseBrightness: { min: 0, max: 100 },
  fullBrightness: { min: 0, max: 100 },
  lowerLuxThreshold: { min: 0, max: 1000 },
  upperLuxThreshold: { min: 0, max: 1000 },
};

const PAIRED_DAYLIGHT_FIELDS: Record<string, string[]> = {
  lowerLuxThreshold: ["upperLuxThreshold"],
  upperLuxThreshold: ["lowerLuxThreshold"],
  baseBrightness: ["fullBrightness"],
  fullBrightness: ["baseBrightness"],
};

export function sanitizeNumericInput(raw: any, min: number, max: number): number {
  const trimmed = String(raw).trim();

  if (trimmed === "") return min;

  if (trimmed.startsWith("-")) return min;

  const digitsOnly = trimmed.replace(/[^0-9]/g, "");
  if (digitsOnly === "") return min;

  const withoutLeadingZeros = digitsOnly.replace(/^0+/, "") || "0";
  const num = Number(withoutLeadingZeros);
  if (isNaN(num)) return min;

  return Math.min(Math.max(num, min), max);
}

export function computeDaylightProfileErrors(values: DaylightProfileValues): DaylightProfileValidationErrors {
  const errs: DaylightProfileValidationErrors = {};

  if (!values.activeStartTime) {
    errs.activeStartTime = "Start time is required";
  }

  if (!values.activeEndTime) {
    errs.activeEndTime = "End time is required";
  }

  if (values.baseBrightness === null || values.baseBrightness === undefined) {
    errs.baseBrightness = "Base Brightness is required";
  }

  if (values.fullBrightness === null || values.fullBrightness === undefined) {
    errs.fullBrightness = "Full Brightness is required";
  }

  if (values.lowerLuxThreshold === null || values.lowerLuxThreshold === undefined) {
    errs.lowerLuxThreshold = "Lower Lux Threshold is required";
  }

  if (values.upperLuxThreshold === null || values.upperLuxThreshold === undefined) {
    errs.upperLuxThreshold = "Upper Lux Threshold is required";
  }

  if (values.lowerAction === null || values.lowerAction === undefined) {
    errs.lowerAction = "Lower action is required";
  }

  if (values.upperAction === null || values.upperAction === undefined) {
    errs.upperAction = "Upper action is required";
  }

  if (values.zoneId === null || values.zoneId === undefined) {
    errs.zoneId = "Zone is required";
  }

  if (
    values.lowerLuxThreshold !== null &&
    values.upperLuxThreshold !== null &&
    values.upperLuxThreshold <= values.lowerLuxThreshold
  ) {
    errs.upperLuxThreshold = "Upper Lux Threshold must be greater than Lower Lux Threshold";
  }

  if (
    values.baseBrightness !== null &&
    values.fullBrightness !== null &&
    values.fullBrightness <= values.baseBrightness
  ) {
    errs.fullBrightness = "Full Brightness must be greater than Base Brightness";
  }

  const lc = DAYLIGHT_PROFILE_FIELD_CONFIG;
  if (values.baseBrightness !== null && (values.baseBrightness < lc.baseBrightness.min || values.baseBrightness > lc.baseBrightness.max)) {
    errs.baseBrightness = `Value must be between ${lc.baseBrightness.min} and ${lc.baseBrightness.max}`;
  }
  if (values.fullBrightness !== null && (values.fullBrightness < lc.fullBrightness.min || values.fullBrightness > lc.fullBrightness.max)) {
    errs.fullBrightness = `Value must be between ${lc.fullBrightness.min} and ${lc.fullBrightness.max}`;
  }
  if (values.lowerLuxThreshold !== null && (values.lowerLuxThreshold < lc.lowerLuxThreshold.min || values.lowerLuxThreshold > lc.lowerLuxThreshold.max)) {
    errs.lowerLuxThreshold = `Value must be between ${lc.lowerLuxThreshold.min} and ${lc.lowerLuxThreshold.max}`;
  }
  if (values.upperLuxThreshold !== null && (values.upperLuxThreshold < lc.upperLuxThreshold.min || values.upperLuxThreshold > lc.upperLuxThreshold.max)) {
    errs.upperLuxThreshold = `Value must be between ${lc.upperLuxThreshold.min} and ${lc.upperLuxThreshold.max}`;
  }

  return errs;
}

export function useDaylightProfileValidation(values: DaylightProfileValues) {
  const [errors, setErrors] = useState<DaylightProfileValidationErrors>({});
  const [visibleErrorFields, setVisibleErrorFields] = useState<Set<string>>(new Set());
  const [hasSubmitted, setHasSubmitted] = useState(false);

  const touchField = (fieldName: string) => {
    setVisibleErrorFields((prev) => {
      const next = new Set(prev);
      next.add(fieldName);
      const paired = PAIRED_DAYLIGHT_FIELDS[fieldName];
      if (paired) {
        paired.forEach((f) => next.add(f));
      }
      return next;
    });
  };

  const validate = () => {
    const allFields = [
      "activeStartTime",
      "activeEndTime",
      "baseBrightness",
      "fullBrightness",
      "lowerLuxThreshold",
      "upperLuxThreshold",
      "lowerAction",
      "upperAction",
      "zoneId",
    ];
    setHasSubmitted(true);
    setVisibleErrorFields(new Set(allFields));
    const errs = computeDaylightProfileErrors(values);
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  useEffect(() => {
    const errs = computeDaylightProfileErrors(values);
    setErrors(errs);
  }, [values]);

  const displayedErrors = useMemo(() => {
    const result: DaylightProfileValidationErrors = {};
    if (!hasSubmitted) {
      return result;
    }

    visibleErrorFields.forEach((field) => {
      if (errors[field]) {
        result[field] = errors[field];
      }
    });
    return result;
  }, [errors, visibleErrorFields]);

  const setFieldError = (fieldName: string, message: string) => {
    setErrors((prev) => ({ ...prev, [fieldName]: message }));
    setVisibleErrorFields((prev) => new Set(prev).add(fieldName));
  };

  const reset = () => {
    setErrors({});
    setVisibleErrorFields(new Set());
    setHasSubmitted(false);
  };

  return {
    errors,
    visibleErrorFields,
    displayedErrors,
    touchField,
    validate,
    setFieldError,
    reset,
  };
}
